// Shopify API Configuration
const SHOPIFY_API_VERSION = '2025-07';
const SHOPIFY_STORE_PERMANENT_DOMAIN = 'shakatah.myshopify.com';
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
const SHOPIFY_STOREFRONT_TOKEN = '99800fc132b34a23a04de1edcf2c1874';

// Dynamic toast import to avoid module-level execution issues
async function showToastError(title: string, description: string) {
  const { toast } = await import("sonner");
  toast.error(title, { description });
}

// Types
export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    shakatahEnabled?: {
      value: string;
    } | null;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: {
            amount: string;
            currencyCode: string;
          };
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
  };
}

// Helper to check if Shakatah is enabled for a product
export function isShakatahEnabled(product: ShopifyProduct | ShopifyProduct["node"]): boolean {
  const node = 'node' in product ? product.node : product;
  return node.shakatahEnabled?.value === 'true';
}

// GraphQL Queries
const STOREFRONT_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          description
          handle
          shakatahEnabled: metafield(namespace: "custom", key: "shakatah_enabled") {
            value
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
        }
      }
    }
  }
`;

const PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      description
      handle
      shakatahEnabled: metafield(namespace: "custom", key: "shakatah_enabled") {
        value
      }
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 10) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 100) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
      options {
        name
        values
      }
    }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                  }
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) {
      id
      totalQuantity
    }
  }
`;

// Check if a Shopify cart still exists and has items
export async function checkShopifyCart(cartId: string): Promise<{ exists: boolean; hasItems: boolean }> {
  try {
    console.log('Checking Shopify cart status for:', cartId);
    const data = await storefrontApiRequest(CART_QUERY, { id: cartId });
    
    // If API returns null/undefined, the cart doesn't exist
    if (!data) {
      console.log('Shopify API returned no data, cart likely completed');
      return { exists: false, hasItems: false };
    }
    
    const cart = data?.data?.cart;
    
    // Check for GraphQL errors indicating cart not found
    if (data?.errors?.length > 0) {
      console.log('Shopify returned errors:', data.errors);
      return { exists: false, hasItems: false };
    }
    
    if (!cart) {
      console.log('Cart not found in Shopify response, checkout completed');
      return { exists: false, hasItems: false };
    }
    
    console.log('Cart exists with totalQuantity:', cart.totalQuantity);
    
    // After checkout completion, Shopify sets totalQuantity to 0
    return { 
      exists: true, 
      hasItems: cart.totalQuantity > 0 
    };
  } catch (error) {
    console.error('Error checking cart status:', error);
    // If there's an error checking, assume cart doesn't exist (safer to clear)
    return { exists: false, hasItems: false };
  }
}

// API Helper
export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (response.status === 402) {
    showToastError("Shopify: Payment required", "Shopify API access requires an active Shopify billing plan. Visit https://admin.shopify.com to upgrade.");
    throw new Error('Payment required');
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  
  if (data.errors) {
    throw new Error(`Error calling Shopify: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  return data;
}

// Fetch Products
export async function fetchProducts(first: number = 20, query?: string): Promise<ShopifyProduct[]> {
  const data = await storefrontApiRequest(STOREFRONT_QUERY, { first, query });
  return data.data.products.edges;
}

// Fetch Single Product by Handle
export async function fetchProductByHandle(handle: string) {
  const data = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, { handle });
  return data.data.productByHandle;
}

// Create Checkout
export interface CartItem {
  lineId?: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  quantity: number;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
}

export interface CheckoutResult {
  checkoutUrl: string;
  cartId: string;
}

export async function createStorefrontCheckout(items: CartItem[]): Promise<CheckoutResult> {
  try {
    const lines = items.map(item => ({
      quantity: item.quantity,
      merchandiseId: item.variantId,
    }));

    const cartData = await storefrontApiRequest(CART_CREATE_MUTATION, {
      input: { lines },
    });

    if (cartData.data.cartCreate.userErrors.length > 0) {
      throw new Error(`Cart creation failed: ${cartData.data.cartCreate.userErrors.map((e: { message: string }) => e.message).join(', ')}`);
    }

    const cart = cartData.data.cartCreate.cart;
    
    if (!cart.checkoutUrl) {
      throw new Error('No checkout URL returned from Shopify');
    }

    const url = new URL(cart.checkoutUrl);
    url.searchParams.set('channel', 'online_store');
    
    return {
      checkoutUrl: url.toString(),
      cartId: cart.id, // This is the actual Shopify cart GID
    };
  } catch (error) {
    console.error('Error creating storefront checkout:', error);
    throw error;
  }
}
